// API client for communicating with Cloudflare Workers backend

import { 
  ApiResponse, 
  AuthRequest, 
  AuthResponse, 
  ErrorResponse,
  GetPitchesResponse,
  GetContentResponse,
  CreateContentRequest,
  UpdateContentRequest,
  GenerateContentRequest,
  PublishContentRequest,
  PublishContentResponse,
  AnalyticsRequest,
  AnalyticsResponse
} from '../../shared/types'

export class ApiClient {
  private baseUrl: string
  private token: string | null = null

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl
    this.loadToken()
  }

  private loadToken(): void {
    this.token = localStorage.getItem('authToken')
  }

  setToken(token: string): void {
    this.token = token
    localStorage.setItem('authToken', token)
  }

  clearToken(): void {
    this.token = null
    localStorage.removeItem('authToken')
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }

    return headers
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers
      }
    })

    const data = await response.json()

    if (!response.ok) {
      const error = data as ErrorResponse
      throw new ApiError(error.message || 'Request failed', response.status, error.error)
    }

    const apiResponse = data as ApiResponse<T>
    if (!apiResponse.success) {
      throw new ApiError(apiResponse.error || 'Request failed', response.status)
    }

    return apiResponse.data as T
  }

  // Authentication
  async login(credentials: AuthRequest): Promise<AuthResponse> {
    const data = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    })
    
    this.setToken(data.token)
    return data
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' })
    } finally {
      this.clearToken()
    }
  }

  async getCurrentUser(): Promise<AuthResponse['user']> {
    return this.request<AuthResponse['user']>('/auth/session')
  }

  // Content Pitches
  async getPitches(): Promise<GetPitchesResponse> {
    return this.request<GetPitchesResponse>('/pitches')
  }

  async generateNewPitches(): Promise<GetPitchesResponse> {
    return this.request<GetPitchesResponse>('/pitches', {
      method: 'POST'
    })
  }

  // Content Management
  async getContent(): Promise<GetContentResponse> {
    return this.request<GetContentResponse>('/content')
  }

  async getContentItem(id: string) {
    return this.request(`/content/${id}`)
  }

  async createContent(data: CreateContentRequest) {
    return this.request('/content', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateContent(id: string, data: UpdateContentRequest) {
    return this.request(`/content/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async generateContent(data: GenerateContentRequest) {
    return this.request('/content/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // Social Media
  async getSocialAccounts() {
    return this.request('/social/accounts')
  }

  async connectSocialAccount(platform: string, code: string, state: string) {
    return this.request(`/social/connect/${platform}`, {
      method: 'POST',
      body: JSON.stringify({ code, state })
    })
  }

  async publishContent(data: PublishContentRequest): Promise<PublishContentResponse> {
    return this.request<PublishContentResponse>('/social/publish', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // Analytics
  async getAnalytics(params: AnalyticsRequest = {}): Promise<AnalyticsResponse> {
    const queryParams = new URLSearchParams()
    
    if (params.period) queryParams.set('period', params.period)
    if (params.platform) queryParams.set('platform', params.platform.toString())
    if (params.startDate) queryParams.set('startDate', params.startDate.toISOString())
    if (params.endDate) queryParams.set('endDate', params.endDate.toISOString())

    const endpoint = `/analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return this.request<AnalyticsResponse>(endpoint)
  }

  async getEngagementData(period: string = '30d') {
    return this.request(`/analytics/engagement?period=${period}`)
  }

  async getContentPerformance() {
    return this.request('/analytics/performance')
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Create singleton instance
export const apiClient = new ApiClient(
  process.env.NODE_ENV === 'development' 
    ? 'http://localhost:8787/api' // Wrangler dev server
    : 'https://benki-api.adityaiyer-avi.workers.dev/api' // Production
)

// React Query helpers
export const apiKeys = {
  all: ['api'] as const,
  pitches: () => [...apiKeys.all, 'pitches'] as const,
  content: () => [...apiKeys.all, 'content'] as const,
  contentItem: (id: string) => [...apiKeys.content(), id] as const,
  socialAccounts: () => [...apiKeys.all, 'social', 'accounts'] as const,
  analytics: (params?: AnalyticsRequest) => [...apiKeys.all, 'analytics', params] as const,
  user: () => [...apiKeys.all, 'user'] as const
} as const